/**
 * =========================================================
 * Përditësuar: 2026-05-23 (Europe/Warsaw)
 * PromoRibbonStrip — Shiriti Promocional Global
 * =========================================================
 * Qëllimi: Shfaq njoftimet/ofertat aktive nga DB.
 * Endpoint: GET /public/promo-ribbon/feed?zone_id=HOME_TOP
 * Stilimi: bg_color + text_color vijnë nga API (dinamike).
 * Vendosja: Mbi Header në AppShell.tsx.
 * =========================================================
 */

import { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/lib/api/client";

interface PromoRibbonItem {
  id: number;
  title: string;
  href: string;
  bg_color: string;
  text_color: string;
  image_url: string | null;
}

interface PromoRibbonFeed {
  zone_id: string;
  items: PromoRibbonItem[];
  generated_at: string;
}

const fetchRibbonFeed = async (): Promise<PromoRibbonFeed> => {
  const r = await apiClient.get<PromoRibbonFeed>("/public/promo-ribbon/feed", {
    params: { zone_id: "HOME_TOP" },
  });
  return r.data;
};

export function PromoRibbonStrip(): JSX.Element | null {
  const { data } = useQuery({
    queryKey: ["promo-ribbon", "HOME_TOP"],
    queryFn: fetchRibbonFeed,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });

  const [currentIdx, setCurrentIdx] = useState(0);
  const [dismissed, setDismissed] = useState(false);
  const [visible, setVisible] = useState(false);

  const items = data?.items ?? [];

  useEffect(() => {
    if (items.length === 0) return;
    const timer = setTimeout(() => setVisible(true), 80);
    return () => clearTimeout(timer);
  }, [items.length]);

  useEffect(() => {
    if (items.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIdx((i) => (i + 1) % items.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [items.length]);

  if (dismissed || items.length === 0) return null;

  const item = items[currentIdx];

  return (
    <div
      role="banner"
      style={{ backgroundColor: item.bg_color, color: item.text_color }}
      className={`relative flex items-center justify-center gap-3 px-4 py-2.5 text-sm font-medium
                  transition-all duration-500 ease-out
                  ${visible ? "opacity-100 translate-y-0" : "opacity-0 -translate-y-2"}`}
    >
      {item.image_url && (
        <img src={item.image_url} alt="" className="h-5 w-5 rounded object-cover" aria-hidden="true" />
      )}

      <a
        href={item.href}
        className="hover:underline focus:outline-none focus:underline"
        style={{ color: item.text_color }}
      >
        {item.title}
      </a>

      {items.length > 1 && (
        <span className="text-xs opacity-60">
          {currentIdx + 1}/{items.length}
        </span>
      )}

      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Mbyll njoftimin"
        className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-0.5 opacity-70 hover:opacity-100"
        style={{ color: item.text_color }}
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  );
}

/**
 * =========================================================
 * Fund i PromoRibbonStrip
 * =========================================================
 */
